/**
 * The Drive-style filter chips above the documents table (#6559): Type,
 * People, Source, Uploaded, Document date, Status and, with the RMS product, Related
 * to. Each chip is a button that opens its own menu; an active chip fills,
 * names its values and grows a clear control. The chips write filters; they
 * know nothing about the listing.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { Popover, Tooltip } from "@mui/material";
import {
  CaretDownIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  XIcon,
  ArrowRightIcon,
} from "@phosphor-icons/react";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import FilterCheck from "@/components/FpPages/RMS/Search/Filter/FilterCheck";
import {
  QuietScrollStrip,
  revealWithinStrip,
  scrollStripOf,
} from "@/components/common/QuietScrollStrip";
import { formatCalendarDay } from "@/utils/temporal";
import type { DocumentStatusBucket, LinkedEntity } from "./api";
import {
  clauseSummary,
  type ColumnClause,
  type ColumnKind,
  type DocumentDateRange,
  type DocumentFilters,
  type LocationFilter,
  type UploadedPreset,
  type UploadedRange,
  isSourceKey,
} from "./state";
import {
  useDocumentUploaders,
  type DocumentTypeOption,
} from "./useDocumentsSearch";
import type { DocumentColumn } from "@/components/FpPages/RMS/Data/documentColumns";
import { RECORD_KINDS, recordKindLabel } from "./records";
import { searchEntities } from "./api";

export const STATUS_LABELS: Record<DocumentStatusBucket, string> = {
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
  locked: "Locked",
};
// No Failed (round 18i, Hunter): a failed document is ours to fix, never a
// user's filter. The label stays for the rare row that carries the bucket.
const STATUS_ORDER: DocumentStatusBucket[] = [
  "processing",
  "completed",
  "locked",
];

const PRESET_LABELS: Record<Exclude<UploadedPreset, "custom">, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  year: "This year",
};
const PRESET_ORDER: Array<Exclude<UploadedPreset, "custom">> = [
  "today",
  "7d",
  "30d",
  "year",
];

// The first value verbatim, the rest as "+N" (CD ruling D): the chip never
// widens or lists, and adding a value still changes the visible text.
const summarize = (values: string[]): string =>
  values.length <= 1 ? values.join("") : `${values[0]} +${values.length - 1}`;

const rangeLabel = (
  from: string | null | undefined,
  to: string | null | undefined
) => {
  const f = from ? formatCalendarDay(from, "medium") : null;
  const t = to ? formatCalendarDay(to, "medium") : null;
  if (f && t) return `${f} – ${t}`;
  if (f) return `from ${f}`;
  if (t) return `until ${t}`;
  return "";
};

export const uploadedLabel = (range: UploadedRange): string =>
  range.preset === "custom"
    ? rangeLabel(range.from, range.to)
    : PRESET_LABELS[range.preset];

// --- one chip ------------------------------------------------------------------

interface ChipProps {
  name: string;
  /** The active values' summary; empty means inactive. */
  summary: string;
  title?: string;
  /** Clears the chip; absent on a chip that is never active. */
  onClear?: () => void;
  /** Runs however the popover closes — Escape, an outside click, or a
   *  child's own close — for a chip that keeps state across its steps. */
  onClosed?: () => void;
  children: (close: () => void) => React.ReactNode;
}

/** The chips' one popover paper, shared by every menu that opens from the
 *  row (the narrow picker's view chip included, round 9). */
/** A chip's menu is at least this wide; the chip decides which of its
 *  edges the menu hangs from by whether this fits beside it. */
const CHIP_MENU_MIN_WIDTH = 220;

export const chipPopoverSlotProps = {
  paper: {
    sx: {
      mt: 0.5,
      minWidth: CHIP_MENU_MIN_WIDTH,
      borderRadius: "10px",
      border: "1px solid var(--fp-border)",
      boxShadow: "0 8px 24px rgba(23, 25, 28, 0.12)",
    },
  },
};

const Chip: React.FC<ChipProps> = ({
  name,
  summary,
  title,
  onClear,
  onClosed,
  children,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Which edge the menu hangs from: the chip's left, unless the menu would
  // then run past the strip the chip is in.
  const [hangRight, setHangRight] = useState(false);
  const active = summary !== "";
  const close = () => {
    setAnchor(null);
    onClosed?.();
  };
  // A chip in a scrolling strip keeps itself in view while its menu is open
  // (#6559 round 19c, Hunter): a clause pill inserted before the Attributes
  // chip pushes the chip past the strip's fold, and the menu — which MUI
  // re-anchors after every render — followed it out of the picker. Every
  // render, since it is the row re-laying out that moves the chip; the
  // strip is scrolled (never the page) and the menu re-anchors after.
  useLayoutEffect(() => {
    if (anchor === null) return;
    const strip = scrollStripOf(anchor);
    if (!strip) return;
    revealWithinStrip(strip, anchor);
    const right =
      anchor.getBoundingClientRect().left + CHIP_MENU_MIN_WIDTH >
      strip.getBoundingClientRect().right;
    setHangRight((prev) => (prev === right ? prev : right));
  });
  return (
    <span className="inline-flex items-stretch">
      {/* describeChild keeps the chip's accessible name its visible text; the
          full value list is a description, not a label. */}
      <Tooltip
        title={title ?? ""}
        placement="bottom"
        enterDelay={600}
        describeChild
      >
        <button
          type="button"
          data-active={active ? "true" : "false"}
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          onClick={(e) => setAnchor(e.currentTarget)}
          // The design system's control chip (RMS/design/Button): a 7px
          // hairline control on the surface; an active state takes the ink
          // border, since colour means judgement or category everywhere else.
          className={`inline-flex h-[30px] items-center gap-1.5 border bg-[var(--fp-surface)] px-3 text-[12.5px] font-medium shadow-[var(--fp-lift)] transition-colors ${
            active
              ? "rounded-l-[7px] border-r-0 border-[var(--fp-ink)] text-[var(--fp-ink)]"
              : "rounded-[7px] border-[var(--fp-border-strong)] text-[var(--fp-ink-2)] hover:border-[var(--fp-ink)] hover:text-[var(--fp-ink)]"
          }`}
        >
          <span className="max-w-[260px] truncate">
            {active ? `${name}: ${summary}` : name}
          </span>
          <CaretDownIcon size={12} aria-hidden />
        </button>
      </Tooltip>
      {active && onClear && (
        <button
          type="button"
          aria-label={`Clear ${name} filter`}
          onClick={onClear}
          className="inline-flex h-[30px] items-center rounded-r-[7px] border border-l-0 border-[var(--fp-ink)] bg-[var(--fp-surface)] px-2 text-[var(--fp-ink)] shadow-[var(--fp-lift)] hover:bg-[var(--fp-ink)] hover:text-[var(--fp-surface)]"
        >
          <XIcon size={12} aria-hidden />
        </button>
      )}
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: hangRight ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: hangRight ? "right" : "left",
        }}
        slotProps={chipPopoverSlotProps}
      >
        {anchor !== null && children(close)}
      </Popover>
    </span>
  );
};

export const menuItemClass =
  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-[var(--fp-ink)] hover:bg-[var(--fp-hover-row)]";

interface CheckListProps<K extends string> {
  label: string;
  /** A swatch beside an option: an enum column's own colour for the value. */
  options: Array<{ key: K; label: string; swatch?: string }>;
  selected: K[];
  onChange: (selected: K[]) => void;
  emptyText?: string;
}

// A menu past this many options gets a search field and lists the selected
// options first (CD ruling A): a long unsearchable column covers the table.
const SEARCHABLE_FROM = 10;

const CheckList = <K extends string>({
  label,
  options,
  selected,
  onChange,
  emptyText = "Nothing to choose from",
}: CheckListProps<K>) => {
  const [q, setQ] = useState("");
  const searchable = options.length > SEARCHABLE_FROM;
  const term = q.trim().toLowerCase();
  const shown = options
    .filter((o) => !term || o.label.toLowerCase().includes(term))
    .sort(
      (a, b) =>
        Number(selected.includes(b.key)) - Number(selected.includes(a.key))
    );
  return (
    <div className="w-[260px]">
      {searchable && (
        <div className="flex items-center gap-2 border-b border-[var(--fp-border)] px-3 py-2">
          <MagnifyingGlassIcon
            size={14}
            className="text-[var(--fp-ink-3)]"
            aria-hidden
          />
          <input
            type="search"
            aria-label={`Search ${label.toLowerCase()}`}
            placeholder={`Search ${label.toLowerCase()}`}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-transparent text-[13px] text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-3)]"
          />
        </div>
      )}
      <div
        role="menu"
        aria-label={label}
        className="max-h-[320px] overflow-y-auto py-1"
      >
        {options.length === 0 && (
          <div className="px-3 py-2 text-[12.5px] text-[var(--fp-ink-3)]">
            {emptyText}
          </div>
        )}
        {options.length > 0 && shown.length === 0 && (
          <div className="px-3 py-2 text-[12.5px] text-[var(--fp-ink-3)]">
            No matches
          </div>
        )}
        {shown.map((option) => {
          const checked = selected.includes(option.key);
          return (
            <button
              key={option.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={checked}
              className={menuItemClass}
              onClick={() =>
                onChange(
                  checked
                    ? selected.filter((k) => k !== option.key)
                    : [...selected, option.key]
                )
              }
            >
              <FilterCheck checked={checked} />
              {option.swatch && (
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: option.swatch }}
                />
              )}
              <span className="truncate">{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const dateInputClass =
  "h-8 rounded-[6px] border border-[var(--fp-border-strong)] px-2 text-[12.5px] text-[var(--fp-ink)]";

interface DayRangeFormProps {
  from: string | null;
  to: string | null;
  onApply: (range: DocumentDateRange) => void;
}

const DayRangeForm: React.FC<DayRangeFormProps> = ({ from, to, onApply }) => {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");
  const valid = draftFrom !== "" || draftTo !== "";
  return (
    <form
      className="flex flex-col gap-2 px-3 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onApply({ from: draftFrom || null, to: draftTo || null });
      }}
    >
      <label className="flex items-center justify-between gap-3 text-[12.5px] text-[var(--fp-ink-2)]">
        From
        <input
          type="date"
          value={draftFrom}
          max={draftTo || undefined}
          onChange={(e) => setDraftFrom(e.target.value)}
          className={dateInputClass}
        />
      </label>
      <label className="flex items-center justify-between gap-3 text-[12.5px] text-[var(--fp-ink-2)]">
        To
        <input
          type="date"
          value={draftTo}
          min={draftFrom || undefined}
          onChange={(e) => setDraftTo(e.target.value)}
          className={dateInputClass}
        />
      </label>
      <button
        type="submit"
        disabled={!valid}
        className="mt-1 h-8 rounded-[6px] bg-[var(--fp-ink)] px-3 text-[12.5px] font-medium text-white disabled:opacity-40"
      >
        Apply
      </button>
    </form>
  );
};

// --- entity search (Related to) ---------------------------------------------

interface EntityPickerProps {
  selected: LinkedEntity[];
  onChange: (entities: LinkedEntity[]) => void;
  /** The kinds to search; every record kind by default. A record-reference
   *  column searches only the kinds it may hold. */
  kinds?: string[];
  /** With exactly one record picked the chip is a ramp to its location:
   *  Go to record leads the list (Claude Design, round 5). */
  onGoToRecord?: (record: LinkedEntity) => void;
}

const EntityPicker: React.FC<EntityPickerProps> = ({
  selected,
  onChange,
  onGoToRecord,
  kinds = RECORD_KINDS,
}) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<LinkedEntity[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);
  const runSearch = useCallback(
    (term: string, searchKinds: readonly string[], id: number) => {
      searchEntities(term, searchKinds).then(
        (found) => {
          if (requestRef.current !== id) return;
          setResults(found);
          setSearching(false);
        },
        () => {
          if (requestRef.current !== id) return;
          setResults([]);
          setSearching(false);
          setFailed(true);
        }
      );
    },
    []
  );
  const debouncedSearch = useDebouncedCallback(runSearch, 150);
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      debouncedSearch.cancel();
      setResults([]);
      return;
    }
    const id = ++requestRef.current;
    setSearching(true);
    setFailed(false);
    debouncedSearch(term, kinds, id);
  }, [q, kinds, debouncedSearch]);

  const shown = [
    ...selected,
    ...results.filter((r) => !selected.some((s) => s.publicId === r.publicId)),
  ];
  return (
    <div className="w-[320px]">
      <div className="flex items-center gap-2 border-b border-[var(--fp-border)] px-3 py-2">
        <MagnifyingGlassIcon
          size={14}
          className="text-[var(--fp-ink-3)]"
          aria-hidden
        />
        <input
          type="search"
          aria-label="Search records"
          placeholder="Search records"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full bg-transparent text-[13px] text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-3)]"
        />
      </div>
      <div
        role="menu"
        aria-label="Related to"
        className="max-h-[300px] overflow-y-auto py-1"
      >
        {onGoToRecord && selected.length === 1 && (
          <button
            type="button"
            role="menuitem"
            className={`${menuItemClass} border-b border-[var(--fp-border)] font-medium`}
            onClick={() => onGoToRecord(selected[0]!)}
          >
            <ArrowRightIcon size={14} aria-hidden />
            Go to record
          </button>
        )}
        {shown.length === 0 && (
          <div className="px-3 py-2 text-[12.5px] text-[var(--fp-ink-3)]">
            {searching
              ? "Searching…"
              : failed
                ? "The search could not be run"
                : q.trim()
                  ? "No matches"
                  : "Type to search"}
          </div>
        )}
        {shown.map((entity) => {
          const checked = selected.some((s) => s.publicId === entity.publicId);
          return (
            <button
              key={entity.publicId}
              type="button"
              role="menuitemcheckbox"
              aria-checked={checked}
              className={menuItemClass}
              onClick={() =>
                onChange(
                  checked
                    ? selected.filter((s) => s.publicId !== entity.publicId)
                    : [...selected, entity]
                )
              }
            >
              <FilterCheck checked={checked} />
              <span className="truncate">{entity.name}</span>
              <span className="ml-auto shrink-0 text-[11.5px] text-[var(--fp-ink-3)]">
                {recordKindLabel(entity.kind)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// --- custom attributes (round 8b) ---------------------------------------------

/** The editor a column earns: its data type folded to the six the chips know.
 *  An enum is a string column with a fixed value list; percentage reads as a
 *  number. */
export const columnKindOf = (column: DocumentColumn): ColumnKind => {
  switch (column.data_type) {
    case "entity":
      return "entity";
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "number":
    case "percentage":
      return "number";
    default:
      return column.is_enum ? "enum" : "text";
  }
};

const numberInputClass =
  "h-8 w-[104px] rounded-[6px] border border-[var(--fp-border-strong)] px-2 text-[12.5px] text-[var(--fp-ink)]";

/** Min / max for a number column; either bound may stay empty. */
const MinMaxForm: React.FC<{
  from: string | null;
  to: string | null;
  onApply: (range: { from: string | null; to: string | null }) => void;
}> = ({ from, to, onApply }) => {
  const [draftFrom, setDraftFrom] = useState(from ?? "");
  const [draftTo, setDraftTo] = useState(to ?? "");
  const valid = draftFrom !== "" || draftTo !== "";
  return (
    <form
      className="flex flex-col gap-2 px-3 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onApply({ from: draftFrom || null, to: draftTo || null });
      }}
    >
      <label className="flex items-center justify-between gap-3 text-[12.5px] text-[var(--fp-ink-2)]">
        Min
        <input
          type="number"
          step="any"
          value={draftFrom}
          onChange={(e) => setDraftFrom(e.target.value)}
          className={numberInputClass}
        />
      </label>
      <label className="flex items-center justify-between gap-3 text-[12.5px] text-[var(--fp-ink-2)]">
        Max
        <input
          type="number"
          step="any"
          value={draftTo}
          onChange={(e) => setDraftTo(e.target.value)}
          className={numberInputClass}
        />
      </label>
      <button
        type="submit"
        disabled={!valid}
        className="mt-1 h-8 rounded-[6px] bg-[var(--fp-ink)] px-3 text-[12.5px] font-medium text-white disabled:opacity-40"
      >
        Apply
      </button>
    </form>
  );
};

/** One field for a text column: contains. */
const ContainsForm: React.FC<{
  text: string;
  onApply: (text: string) => void;
}> = ({ text, onApply }) => {
  const [draft, setDraft] = useState(text);
  const valid = draft.trim() !== "";
  return (
    <form
      className="flex flex-col gap-2 px-3 py-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onApply(draft.trim());
      }}
    >
      <label className="flex items-center justify-between gap-3 text-[12.5px] text-[var(--fp-ink-2)]">
        Contains
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-8 w-[180px] rounded-[6px] border border-[var(--fp-border-strong)] px-2 text-[12.5px] text-[var(--fp-ink)]"
        />
      </label>
      <button
        type="submit"
        disabled={!valid}
        className="mt-1 h-8 rounded-[6px] bg-[var(--fp-ink)] px-3 text-[12.5px] font-medium text-white disabled:opacity-40"
      >
        Apply
      </button>
    </form>
  );
};

interface ClauseEditorProps {
  column: DocumentColumn;
  clause: ColumnClause | null;
  /** A finished clause; `null` clears it (an enum with no value ticked). */
  onApply: (clause: ColumnClause | null) => void;
  /** Editors that commit on Apply close the popover; the enum list stays
   *  open like every other multi-select. */
  close: () => void;
}

/** The editor per kind (Claude Design, round 8b): enum → multi-select with the
 *  column's colours; boolean → Yes / No; date → the Document date range
 *  control; number → min / max; record reference → the Related to search;
 *  text → one contains field. */
const ClauseEditor: React.FC<ClauseEditorProps> = ({
  column,
  clause,
  onApply,
  close,
}) => {
  const kind = columnKindOf(column);
  const base = { columnId: column.id, columnName: column.name, kind };
  switch (kind) {
    case "enum": {
      const values = column.scalar_values ?? [];
      const colors = column.enum_colors ?? {};
      return (
        <CheckList
          label={column.name}
          options={values.map((value) => ({
            key: value,
            label: value,
            swatch: colors[value],
          }))}
          selected={clause?.values ?? []}
          onChange={(selected) =>
            onApply(
              selected.length ? { ...base, op: "in", values: selected } : null
            )
          }
          emptyText="No values configured"
        />
      );
    }
    case "boolean":
      return (
        <div role="menu" aria-label={column.name} className="w-[200px] py-1">
          {[true, false].map((value) => {
            const checked = clause?.value === value;
            return (
              <button
                key={String(value)}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                className={menuItemClass}
                onClick={() => {
                  onApply({ ...base, op: "is", value });
                  close();
                }}
              >
                <span className="w-4 text-[var(--fp-accent)]" aria-hidden>
                  {checked && <CheckIcon size={12} weight="bold" />}
                </span>
                {value ? "Yes" : "No"}
              </button>
            );
          })}
        </div>
      );
    case "date":
      return (
        <DayRangeForm
          from={clause?.from ?? null}
          to={clause?.to ?? null}
          onApply={(range) => {
            onApply({ ...base, op: "between", from: range.from, to: range.to });
            close();
          }}
        />
      );
    case "number":
      return (
        <MinMaxForm
          from={clause?.from ?? null}
          to={clause?.to ?? null}
          onApply={(range) => {
            onApply({ ...base, op: "between", from: range.from, to: range.to });
            close();
          }}
        />
      );
    case "entity": {
      const selected: LinkedEntity[] = (clause?.values ?? []).map(
        (publicId, index) => ({
          kind: clause?.kinds?.[index] ?? "",
          publicId,
          name: clause?.labels?.[index] ?? publicId,
        })
      );
      const kinds = column.entity_types ?? undefined;
      return (
        <EntityPicker
          selected={selected}
          kinds={kinds && kinds.length > 0 ? kinds : undefined}
          onChange={(entities) =>
            onApply(
              entities.length
                ? {
                    ...base,
                    op: "in",
                    values: entities.map((e) => e.publicId),
                    labels: entities.map((e) => e.name),
                    kinds: entities.map((e) => e.kind),
                  }
                : null
            )
          }
        />
      );
    }
    case "text":
      return (
        <ContainsForm
          text={clause?.text ?? ""}
          onApply={(text) => {
            onApply({ ...base, op: "contains", text });
            close();
          }}
        />
      );
  }
};

/** The Attributes chip's menu: the organization's columns in its order,
 *  searchable past ten; a pick swaps to that column's editor. */
const ColumnMenu: React.FC<{
  columns: DocumentColumn[];
  onPick: (column: DocumentColumn) => void;
}> = ({ columns, onPick }) => {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const shown = columns.filter(
    (c) => !term || c.name.toLowerCase().includes(term)
  );
  return (
    <div className="w-[260px]">
      {columns.length > SEARCHABLE_FROM && (
        <div className="flex items-center gap-2 border-b border-[var(--fp-border)] px-3 py-2">
          <MagnifyingGlassIcon
            size={14}
            className="text-[var(--fp-ink-3)]"
            aria-hidden
          />
          <input
            type="search"
            aria-label="Search attributes"
            placeholder="Search attributes"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-transparent text-[13px] text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-3)]"
          />
        </div>
      )}
      <div
        role="menu"
        aria-label="Attributes"
        className="max-h-[320px] overflow-y-auto py-1"
      >
        {shown.length === 0 && (
          <div className="px-3 py-2 text-[12.5px] text-[var(--fp-ink-3)]">
            No matches
          </div>
        )}
        {shown.map((column) => (
          <button
            key={column.id}
            type="button"
            role="menuitem"
            className={menuItemClass}
            onClick={() => onPick(column)}
          >
            <span className="truncate">{column.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// --- the row ------------------------------------------------------------------

/** One choice on the Location chip's menu (round 18): its label and the
 *  filter it sets; null is anywhere. */
export interface LocationOption {
  key: string;
  label: string;
  value: LocationFilter | null;
}

/** The Location chip (round 18, Hunter, Drive's Location menu): where a
 *  search looks. The host names the choices from the places it knows and
 *  says what the current value is called. */
export interface LocationChipProps {
  label: string | null;
  options: LocationOption[];
}

const sameLocation = (
  a: LocationFilter | null,
  b: LocationFilter | null
): boolean => {
  if (a === null || b === null) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind !== "place" || b.kind !== "place") return true;
  // Both places: the same drive and the same folder.
  return (
    (a.driveId ?? null) === (b.driveId ?? null) &&
    a.path.length === b.path.length &&
    a.path.every((segment, i) => segment === b.path[i])
  );
};

/** Which sources the Source chip may offer (#15170), and where the answer
 *  that decides it stands: being read, failed, or in. */
export interface SourceGate {
  offers: (key: string) => boolean;
  status: "loading" | "failed" | "ready";
}

export interface FilterChipsProps {
  filters: DocumentFilters;
  onChange: (filters: DocumentFilters) => void;
  typeOptions: DocumentTypeOption[];
  rmsEnabled: boolean;
  /** Chips the surface hides — a view that already fixes a status, say, or
   *  the record view, where the record is the relation. */
  hide?: Array<
    | "status"
    | "people"
    | "source"
    | "entities"
    | "type"
    | "uploaded"
    | "documentDate"
    | "location"
  >;
  /** The Location chip's choices and the current value's name; absent, no
   *  chip (the picker searches the place it stands in). */
  location?: LocationChipProps;
  /** Rendered before the first chip: the picker's view toggles. */
  leading?: React.ReactNode;
  /** Page only: opening a record as a location from the Related to chip. */
  onOpenRecord?: (record: LinkedEntity) => void;
  /** The organization's custom document columns (round 8b). With any, an
   *  Attributes chip stands last in the row; each clause is its own pill. */
  columns?: DocumentColumn[];
  /** The kinds the Attributes menu offers — the picker keeps to enums and
   *  booleans, the page offers every column. */
  columnKinds?: ColumnKind[];
  /** The Source chip's offer, when the view is about a set of sources
   *  (#11635 Round 4: the portals view lists its services only). Absent,
   *  every system uploader the facet knows. */
  sourceKeys?: string[];
  /** Which sources may be offered at all, on any view (#15170: never a
   *  portal hidden from the viewer), and what the list says while it has
   *  none to offer. Absent, every one may. */
  sourceGate?: SourceGate;
  /** Phone: six chips wrap onto two lines and cost a third of the first
   *  screen before a single document appears (Hunter, round 7i). One
   *  scrolling row instead — the same treatment the picker's phone sheet and
   *  the app's tab strips already use, so nothing is hidden and nothing is
   *  taller than one row.
   *
   *  `leading` stays OUT of the strip in this mode. It is the search field,
   *  and a shrinkable flex item beside chips that hold their natural width
   *  collapses to its own padding — measured at 66px, all of it the icon
   *  gutter, so the placeholder never appeared (round 7i review). Search is
   *  how a document is found on a phone; it takes its own row. */
  scrolling?: boolean;
  /** The desktop picker's row (round 9): one line that never wraps, grown to
   *  its strip so the search field in `leading` can take the slack. */
  strip?: boolean;
}

/** The People and Source chips' lists. Each mounts when its chip opens and
 *  unmounts when it closes, so the read behind them runs at the moment a
 *  name is about to show and never on a page mount (#12073). One query
 *  behind both: People lists the viewer, the users and the senders; Source
 *  the rest. */
const UploaderOptions: React.FC<{
  label: "People" | "Source";
  selected: string[];
  onChange: (selected: string[]) => void;
  /** Source only: the keys the chip may offer, when the view is about a
   *  set of sources (the portals view lists its services and no connector). */
  sourceKeys?: string[];
  /** Source only: which sources may be offered at all. */
  sourceGate?: SourceGate;
}> = ({ label, selected, onChange, sourceKeys, sourceGate }) => {
  const uploaders = useDocumentUploaders(true);
  const own = (key: string) =>
    label === "Source" ? isSourceKey(key) : !isSourceKey(key);
  const options = (uploaders.data ?? []).filter(
    (u) =>
      own(u.key) &&
      (label !== "Source" ||
        ((sourceKeys === undefined || sourceKeys.includes(u.key)) &&
          (sourceGate === undefined || sourceGate.offers(u.key))))
  );
  // The chip owns its half of the keys; the other chip's picks ride along.
  const others = selected.filter((key) => !own(key));
  // The Source list waits on its gate as on its own read, and after a
  // failed gate says nothing about whether sources exist.
  const gateStatus = label === "Source" ? sourceGate?.status : undefined;
  return (
    <CheckList
      label={label}
      options={options}
      selected={selected.filter(own)}
      onChange={(keys) => onChange([...others, ...keys])}
      emptyText={
        uploaders.isPending || gateStatus === "loading"
          ? "Loading…"
          : gateStatus === "failed"
            ? "No sources to show"
            : label === "Source"
              ? "No sources yet"
              : "No uploads yet"
      }
    />
  );
};

const FilterChips: React.FC<FilterChipsProps> = ({
  filters,
  onChange,
  typeOptions,
  rmsEnabled,
  hide = [],
  leading,
  location,
  onOpenRecord,
  scrolling = false,
  strip = false,
  columns = [],
  columnKinds,
  sourceKeys: offeredSourceKeys,
  sourceGate,
}) => {
  const set = (patch: Partial<DocumentFilters>) =>
    onChange({ ...filters, ...patch });
  const offered = columns.filter(
    (c) => !columnKinds || columnKinds.includes(columnKindOf(c))
  );
  const columnById = new Map(columns.map((c) => [c.id, c]));
  // The Attributes chip's two steps: the column list, then that column's
  // editor. The pick is forgotten when the popover closes.
  const [picked, setPicked] = useState<DocumentColumn | null>(null);
  // A re-edited clause keeps its place in the row; a new one goes last.
  const setClause = (columnId: number, clause: ColumnClause | null) => {
    const index = filters.columns.findIndex((c) => c.columnId === columnId);
    const next = filters.columns.filter((c) => c.columnId !== columnId);
    if (clause) next.splice(index === -1 ? next.length : index, 0, clause);
    set({ columns: next });
  };
  const typeLabel = (key: string) =>
    typeOptions.find((t) => t.key === key)?.label ?? key;
  // The People options are read only when a name is about to show (#12073):
  // here, for the chip's summary of a filter already set; and in the chip's
  // own list when it opens. One query behind both.
  const uploaders = useDocumentUploaders(filters.uploaders.length > 0);
  // A source key names its system after the prefix, so a service with no
  // documents yet (a Connectors line or an alert lands on it) still reads
  // as the service rather than as its key (#11635 Round 5).
  const uploaderLabel = (key: string) =>
    uploaders.data?.find((u) => u.key === key)?.label ??
    (isSourceKey(key) ? key.replace(/^system:/, "") : key);
  // One `by` list, two chips: People holds the people, Source the rest
  // (#11635, spec R13), and clearing one keeps the other's picks.
  const peopleKeys = filters.uploaders.filter((key) => !isSourceKey(key));
  const sourceKeys = filters.uploaders.filter(isSourceKey);

  const chips = (
    <>
      {!scrolling && leading}
      {!hide.includes("type") && (
        <Chip
          name="Type"
          summary={summarize(filters.types.map(typeLabel))}
          title={filters.types.map(typeLabel).join(", ") || undefined}
          onClear={() => set({ types: [] })}
        >
          {() => (
            <CheckList
              label="Type"
              options={typeOptions}
              selected={filters.types}
              onChange={(types) => set({ types })}
              emptyText="No document types configured"
            />
          )}
        </Chip>
      )}
      {!hide.includes("people") && (
        <Chip
          name="People"
          summary={summarize(peopleKeys.map(uploaderLabel))}
          title={peopleKeys.map(uploaderLabel).join(", ") || undefined}
          onClear={() => set({ uploaders: sourceKeys })}
        >
          {() => (
            <UploaderOptions
              label="People"
              selected={filters.uploaders}
              onChange={(keys) => set({ uploaders: keys })}
            />
          )}
        </Chip>
      )}
      {!hide.includes("source") && (
        <Chip
          name="Source"
          summary={summarize(sourceKeys.map(uploaderLabel))}
          title={sourceKeys.map(uploaderLabel).join(", ") || undefined}
          onClear={() => set({ uploaders: peopleKeys })}
        >
          {() => (
            <UploaderOptions
              label="Source"
              selected={filters.uploaders}
              onChange={(keys) => set({ uploaders: keys })}
              sourceKeys={offeredSourceKeys}
              sourceGate={sourceGate}
            />
          )}
        </Chip>
      )}
      {!hide.includes("uploaded") && (
        <Chip
          name="Uploaded"
          summary={filters.uploaded ? uploadedLabel(filters.uploaded) : ""}
          onClear={() => set({ uploaded: null })}
        >
          {(close) => (
            <div role="menu" aria-label="Uploaded" className="py-1">
              {PRESET_ORDER.map((preset) => {
                const checked = filters.uploaded?.preset === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    role="menuitemradio"
                    aria-checked={checked}
                    className={menuItemClass}
                    onClick={() => {
                      set({ uploaded: { preset } });
                      close();
                    }}
                  >
                    <span className="w-4 text-[var(--fp-accent)]" aria-hidden>
                      {checked && <CheckIcon size={12} weight="bold" />}
                    </span>
                    {PRESET_LABELS[preset]}
                  </button>
                );
              })}
              <div className="mx-3 my-1 border-t border-[var(--fp-border)]" />
              <div className="px-3 pt-1 text-[11.5px] font-medium uppercase tracking-wide text-[var(--fp-ink-3)]">
                Custom range
              </div>
              <DayRangeForm
                from={
                  filters.uploaded?.preset === "custom"
                    ? (filters.uploaded.from ?? null)
                    : null
                }
                to={
                  filters.uploaded?.preset === "custom"
                    ? (filters.uploaded.to ?? null)
                    : null
                }
                onApply={(range) => {
                  set({
                    uploaded: {
                      preset: "custom",
                      from: range.from,
                      to: range.to,
                    },
                  });
                  close();
                }}
              />
            </div>
          )}
        </Chip>
      )}
      {!hide.includes("documentDate") && (
        <Chip
          name="Document date"
          summary={
            filters.documentDate
              ? rangeLabel(filters.documentDate.from, filters.documentDate.to)
              : ""
          }
          onClear={() => set({ documentDate: null })}
        >
          {(close) => (
            <DayRangeForm
              from={filters.documentDate?.from ?? null}
              to={filters.documentDate?.to ?? null}
              onApply={(range) => {
                set({ documentDate: range });
                close();
              }}
            />
          )}
        </Chip>
      )}
      {!hide.includes("status") && (
        <Chip
          name="Status"
          summary={summarize(filters.status.map((s) => STATUS_LABELS[s]))}
          onClear={() => set({ status: [] })}
        >
          {() => (
            <CheckList
              label="Status"
              options={STATUS_ORDER.map((s) => ({
                key: s,
                label: STATUS_LABELS[s],
              }))}
              selected={filters.status}
              onChange={(status) => set({ status })}
            />
          )}
        </Chip>
      )}
      {rmsEnabled && !hide.includes("entities") && (
        <Chip
          name="Related to"
          summary={summarize(filters.entities.map((e) => e.name))}
          title={filters.entities.map((e) => e.name).join(", ") || undefined}
          onClear={() => set({ entities: [] })}
        >
          {(close) => (
            <EntityPicker
              selected={filters.entities}
              onChange={(entities) => set({ entities })}
              onGoToRecord={
                onOpenRecord
                  ? (record) => {
                      close();
                      onOpenRecord(record);
                    }
                  : undefined
              }
            />
          )}
        </Chip>
      )}
      {location && !hide.includes("location") && (
        <Chip
          name="Location"
          summary={location.label ?? ""}
          onClear={() => set({ location: null })}
        >
          {(close) => (
            <div role="menu" aria-label="Location" className="py-1">
              {location.options.map((option) => {
                const checked = sameLocation(option.value, filters.location);
                return (
                  <button
                    key={option.key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={checked}
                    className={menuItemClass}
                    onClick={() => {
                      set({ location: option.value });
                      close();
                    }}
                  >
                    <span className="w-4 text-[var(--fp-accent)]" aria-hidden>
                      {checked && <CheckIcon size={12} weight="bold" />}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </Chip>
      )}
      {filters.columns.map((clause) => {
        const column = columnById.get(clause.columnId);
        return (
          <Chip
            key={`clause:${clause.columnId}`}
            name={clause.columnName}
            summary={clauseSummary(clause) || "…"}
            onClear={() => setClause(clause.columnId, null)}
          >
            {(close) =>
              column ? (
                <ClauseEditor
                  column={column}
                  clause={clause}
                  onApply={(next) => setClause(clause.columnId, next)}
                  close={close}
                />
              ) : (
                <div className="px-3 py-2 text-[12.5px] text-[var(--fp-ink-3)]">
                  This attribute is no longer configured
                </div>
              )
            }
          </Chip>
        );
      })}
      {offered.length > 0 && (
        <Chip name="Attributes" summary="" onClosed={() => setPicked(null)}>
          {(close) => {
            return picked ? (
              <div>
                {/* Focus lands here when the editor opens: the column button
                    that had it is gone, and a popover nothing inside has
                    focus cannot be dismissed from the keyboard. */}
                <button
                  type="button"
                  autoFocus
                  onClick={() => setPicked(null)}
                  className="flex w-full items-center gap-1.5 border-b border-[var(--fp-border)] px-3 py-2 text-left text-[12.5px] font-medium text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]"
                >
                  <ArrowRightIcon
                    size={12}
                    aria-hidden
                    className="rotate-180"
                  />
                  {picked.name}
                </button>
                <ClauseEditor
                  column={picked}
                  clause={
                    filters.columns.find((c) => c.columnId === picked.id) ??
                    null
                  }
                  onApply={(next) => setClause(picked.id, next)}
                  close={close}
                />
              </div>
            ) : (
              <ColumnMenu columns={offered} onPick={setPicked} />
            );
          }}
        </Chip>
      )}
    </>
  );

  // One row that scrolls, or a row that wraps. Nothing is hidden either way.
  // The inner `w-max` is what lets the chips keep their own widths: without
  // it they are flex items in a container the width of the screen and the
  // strip has nothing to scroll.
  return scrolling ? (
    <div className="flex min-w-0 flex-col gap-2">
      {leading}
      <QuietScrollStrip>
        <div className="flex w-max items-center gap-2 whitespace-nowrap">
          {chips}
        </div>
      </QuietScrollStrip>
    </div>
  ) : strip ? (
    <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 whitespace-nowrap">
      {chips}
    </div>
  ) : (
    <div className="flex flex-wrap items-center gap-2">{chips}</div>
  );
};

export default FilterChips;
